import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from 'shared/hooks/useAuth';

export const emptyBusinessCase = () => ({
  key: `bc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  caseDate: '',
  submittedBy: '',
  titleRole: '',
  projectOverview: '',
  backgroundCurrentSituation: '',
  proposedSolution: '',
  technicalRequirementsIntegration: '',
  securityComplianceConsiderations: '',
  implementationPlan: '',
  costEstimate: '',
  benefitsImpactsOnUsers: '',
  performanceMetrics: '',
  risksAndLimitations: '',
});

const META_FIELDS = [
  { name: 'caseDate', label: 'Date' },
  { name: 'submittedBy', label: 'Submitted by' },
  { name: 'titleRole', label: 'Title / Role' },
];

const CONTENT_FIELDS = [
  {
    name: 'projectOverview',
    label: '1. Project Overview',
    hint: 'Briefly describe the problem this project aims to solve or the opportunity it aims to address.',
    required: true,
  },
  {
    name: 'backgroundCurrentSituation',
    label: '2. Background / Current Situation',
    hint: 'Summarize the current context or environment related to the problem or opportunity.',
    required: true,
  },
  {
    name: 'proposedSolution',
    label: '3. Proposed Solution',
    hint: 'Outline the recommended solution, including what it will do and how it addresses the problem.',
    required: true,
  },
  {
    name: 'technicalRequirementsIntegration',
    label: '4. Technical Requirements and Integration',
    hint: 'List major system requirements, platforms, tools, dependencies, or integration points.',
    required: true,
  },
  {
    name: 'securityComplianceConsiderations',
    label: '5. Security and Compliance Considerations',
    hint: 'Note security, privacy, or compliance implications, including data handling and access.',
    required: true,
  },
  {
    name: 'implementationPlan',
    label: '6. Implementation Plan',
    hint: 'List key steps and phases of the rollout, including timelines, teams, and resources.',
    required: true,
  },
  {
    name: 'costEstimate',
    label: '7. Cost Estimate (CAPEX / OPEX)',
    hint: 'Provide a rough CapEx / OpEx breakdown, including ongoing support costs if applicable.',
    required: true,
  },
  {
    name: 'benefitsImpactsOnUsers',
    label: '8. Benefits / Impacts on Users',
    hint: 'Describe anticipated benefits — efficiency, productivity, satisfaction, revenue, etc.',
    required: true,
  },
  {
    name: 'performanceMetrics',
    label: '9. Performance Metrics',
    hint: 'List how success will be measured — KPIs, benchmarks, or usage targets.',
    required: true,
  },
  {
    name: 'risksAndLimitations',
    label: '10. Risks and Limitations',
    hint: 'Outline what might affect success — budget, dependencies, skill gaps, or external factors.',
    required: true,
  },
];

function todayIsoDate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(value) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
}

function roleAsString(role) {
  if (role == null) return '';
  if (typeof role === 'string') return role.trim();
  if (typeof role?.name === 'string') return role.name.trim();
  return '';
}

function resolveLoggedInDefaults(profile) {
  if (!profile) {
    return { caseDate: todayIsoDate(), submittedBy: '', titleRole: '' };
  }
  const name = String(profile.name || profile.fullName || '').trim();
  const email = String(profile.email || '').trim();
  const submittedBy = name && email ? `${name} (${email})` : name || email || '';
  const titleRole =
    roleAsString(profile.role)
    || roleAsString(Array.isArray(profile.roles) ? profile.roles[0] : null)
    || String(profile.jobTitle || '').trim();
  return {
    caseDate: todayIsoDate(),
    submittedBy,
    titleRole,
  };
}

const fieldCardSx = (theme, isDark) => ({
  height: '100%',
  minHeight: 118,
  p: 1.25,
  borderRadius: 2,
  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.14)}`,
  bgcolor: isDark ? theme.palette.background.paper : '#fff',
  boxShadow: isDark ? 'none' : `0 1px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
  display: 'flex',
  flexDirection: 'column',
});

/**
 * Dialog to create / edit / view the Simple Business Case linked 1:1 to Software.
 * Date / Submitted by / Title-Role are taken from the signed-in user and not shown when editing.
 */
export default function BusinessCaseDialog({
  open,
  mode = 'edit',
  initialValues,
  onClose,
  onSave,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const readOnly = mode === 'view';
  const { user } = useAuth();
  const profile = user?.data;
  const loggedInDefaults = useMemo(() => resolveLoggedInDefaults(profile), [profile]);

  const [values, setValues] = useState(emptyBusinessCase);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const base = emptyBusinessCase();
    const incoming = initialValues || {};
    const isNew = !incoming.id && mode !== 'view';
    setValues({
      ...base,
      ...incoming,
      caseDate: isNew
        ? loggedInDefaults.caseDate
        : (incoming.caseDate || loggedInDefaults.caseDate),
      submittedBy: isNew
        ? loggedInDefaults.submittedBy
        : (incoming.submittedBy || loggedInDefaults.submittedBy),
      titleRole: isNew
        ? loggedInDefaults.titleRole
        : (incoming.titleRole || loggedInDefaults.titleRole),
    });
    setErrors({});
  }, [open, initialValues, loggedInDefaults, mode]);

  const setField = (name, value) => {
    if (readOnly) return;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    CONTENT_FIELDS.forEach((field) => {
      if (!field.required) return;
      if (!String(values[field.name] || '').trim()) {
        next[field.name] = `${field.label} is required`;
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (readOnly || !validate()) return;
    onSave({
      ...values,
      caseDate: (values.caseDate || loggedInDefaults.caseDate || '').trim(),
      submittedBy: (values.submittedBy || loggedInDefaults.submittedBy || '').trim(),
      titleRole: (values.titleRole || loggedInDefaults.titleRole || '').trim(),
      projectOverview: (values.projectOverview || '').trim(),
      backgroundCurrentSituation: (values.backgroundCurrentSituation || '').trim(),
      proposedSolution: (values.proposedSolution || '').trim(),
      technicalRequirementsIntegration: (values.technicalRequirementsIntegration || '').trim(),
      securityComplianceConsiderations: (values.securityComplianceConsiderations || '').trim(),
      implementationPlan: (values.implementationPlan || '').trim(),
      costEstimate: (values.costEstimate || '').trim(),
      benefitsImpactsOnUsers: (values.benefitsImpactsOnUsers || '').trim(),
      performanceMetrics: (values.performanceMetrics || '').trim(),
      risksAndLimitations: (values.risksAndLimitations || '').trim(),
    });
  };

  const title = readOnly
    ? 'Business case report'
    : values.id
      ? 'Edit business case'
      : 'Add business case';

  const displayMetaValue = (name) => {
    const raw = values[name] ?? '';
    if (name === 'caseDate') return formatDisplayDate(raw);
    return String(raw || '').trim() || '—';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: isDark ? 'transparent' : alpha(theme.palette.primary.main, 0.015) }}>
        {readOnly ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.25,
              mb: 1.25,
              pt: 0.5,
            }}
          >
            {META_FIELDS.map((field) => (
              <Paper key={field.name} elevation={0} sx={{ ...fieldCardSx(theme, isDark), minHeight: 72 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.82rem' }}>
                  {field.label}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {displayMetaValue(field.name)}
                </Typography>
              </Paper>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1.25,
            alignItems: 'stretch',
            pt: readOnly ? 0 : 0.5,
          }}
        >
          {CONTENT_FIELDS.map((field) => (
            <Paper key={field.name} elevation={0} sx={fieldCardSx(theme, isDark)}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.35, lineHeight: 1.25, fontSize: '0.82rem' }}>
                {field.label}
              </Typography>
              {!readOnly ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: '2.2em',
                    mb: 0.75,
                    fontSize: '0.68rem',
                    lineHeight: 1.3,
                  }}
                >
                  {field.hint}
                </Typography>
              ) : (
                <Box sx={{ mb: 0.75 }} />
              )}
              <Box flex={1} display="flex">
                {readOnly ? (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: String(values[field.name] || '').trim() ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {String(values[field.name] || '').trim() || '—'}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={(e) => setField(field.name, e.target.value)}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ' '}
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter details…"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        alignItems: 'flex-start',
                      },
                      '& textarea': {
                        minHeight: '44px !important',
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly ? (
          <Button variant="contained" color="secondary" onClick={handleSave}>
            Save business case
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
